import React, { useState, useEffect, useCallback } from 'react';
import SafeIcon from '../../common/SafeIcon';
import api from '../../services/api';

const EMPTY_COMPETITOR = { name: '', website: '', industry: '', description: '' };
const EMPTY_AD = {
  competitor_id: '', platform: 'meta', format: 'image', status: 'active',
  title: '', copy: '', landing_page_url: '', cta_text: '',
  analysis_hook: '', analysis_angle: '', analysis_funnel: '', analysis_advisory: ''
};
const EMPTY_CLIENT = { email: '', full_name: '', company_name: '' };

export default function AdminIntel() {
  const [competitors, setCompetitors] = useState([]);
  const [ads, setAds] = useState([]);
  const [clients, setClients] = useState([]);
  const [requests, setRequests] = useState([]);
  const [selectedCompetitorId, setSelectedCompetitorId] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saveToast, setSaveToast] = useState('');

  const [editingCompetitor, setEditingCompetitor] = useState(null);
  const [editingAd, setEditingAd] = useState(null);
  const [newClient, setNewClient] = useState(null);
  const [createdClient, setCreatedClient] = useState(null);

  const showToast = (msg) => {
    setSaveToast(msg);
    setTimeout(() => setSaveToast(''), 2500);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [compRes, clientRes, reqRes] = await Promise.all([
        api.getIntelCompetitors(),
        api.getIntelClients(),
        api.getIntelRequests()
      ]);
      setCompetitors(compRes.competitors || []);
      setClients(clientRes.clients || []);
      setRequests(reqRes.requests || []);
    } catch (err) {
      console.error('Error loading intel data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAds = useCallback(async (competitorId) => {
    try {
      const res = await api.getIntelAds(competitorId);
      setAds(res.ads || []);
    } catch (err) {
      console.error('Error loading ads:', err);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadAds(selectedCompetitorId); }, [selectedCompetitorId, loadAds]);

  const handleSaveCompetitor = async (e) => {
    e.preventDefault();
    try {
      if (editingCompetitor.isNew) {
        await api.createIntelCompetitor(editingCompetitor);
      } else {
        await api.updateIntelCompetitor(editingCompetitor.id, editingCompetitor);
      }
      setEditingCompetitor(null);
      showToast('Competitor saved.');
      loadAll();
    } catch (err) {
      alert(err.message || 'Failed to save competitor.');
    }
  };

  const handleDeleteCompetitor = async (id) => {
    if (!confirm('Remove this competitor and all of its tracked ads?')) return;
    await api.deleteIntelCompetitor(id);
    if (selectedCompetitorId === id) setSelectedCompetitorId('all');
    showToast('Competitor removed.');
    loadAll();
  };

  const handleSaveAd = async (e) => {
    e.preventDefault();
    try {
      if (editingAd.isNew) {
        await api.createIntelAd(editingAd);
      } else {
        await api.updateIntelAd(editingAd.id, editingAd);
      }
      setEditingAd(null);
      showToast('Ad saved. An alert was posted to the client workspace.');
      loadAll();
      loadAds(selectedCompetitorId);
    } catch (err) {
      alert(err.message || 'Failed to save ad.');
    }
  };

  const handleDeleteAd = async (id) => {
    if (!confirm('Remove this ad from the swipe file?')) return;
    await api.deleteIntelAd(id);
    showToast('Ad removed.');
    loadAds(selectedCompetitorId);
    loadAll();
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const res = await api.createIntelClient(newClient);
      setCreatedClient({ ...newClient, tempPassword: res.temp_password });
      setNewClient(null);
      loadAll();
    } catch (err) {
      alert(err.message || 'Failed to create client.');
    }
  };

  const handleToggleAssignment = async (clientId, competitorId, currentlyAssigned) => {
    await api.setIntelClientAssignment(clientId, competitorId, !currentlyAssigned);
    loadAll();
  };

  const handleApproveRequest = async (reqId) => {
    try {
      await api.approveIntelRequest(reqId);
      showToast('Request approved and competitor assigned.');
      loadAll();
    } catch (err) {
      alert(err.message || 'Failed to approve request.');
    }
  };

  if (loading) {
    return <p className="text-zinc-500 text-sm">Loading Client Intelligence data...</p>;
  }

  return (
    <div className="space-y-10 text-white font-sans">
      {saveToast && (
        <div className="p-4 bg-emerald-950/30 border border-emerald-500/20 text-emerald-400 text-xs rounded-xl flex items-center gap-2">
          <SafeIcon name="CheckCircle" className="w-4 h-4" />
          {saveToast}
        </div>
      )}

      {/* Competitors & Ads */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-3xl p-8 shadow-2xl space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-zinc-900 pb-6">
          <div>
            <h2 className="text-xl font-serif font-bold text-white">Competitor Swipe Files</h2>
            <p className="text-xs text-zinc-500 mt-1 font-light">
              Manage the competitors and ad creatives shown in client workspaces. Adding an ad automatically posts an alert to every client tracking that competitor.
            </p>
          </div>
          <button
            onClick={() => setEditingCompetitor({ ...EMPTY_COMPETITOR, isNew: true })}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2 shadow-lg shadow-blue-600/20"
          >
            <SafeIcon name="Plus" className="w-4 h-4" />
            Add Competitor
          </button>
        </div>

        <div className="flex gap-1.5 overflow-x-auto pb-1">
          <button
            onClick={() => setSelectedCompetitorId('all')}
            className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider shrink-0 transition-colors ${selectedCompetitorId === 'all' ? 'bg-blue-600 text-white' : 'bg-zinc-900 text-zinc-400 hover:text-white'}`}
          >
            All Ads
          </button>
          {competitors.map(comp => (
            <button
              key={comp.id}
              onClick={() => setSelectedCompetitorId(comp.id)}
              className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider shrink-0 transition-colors flex items-center gap-2 ${selectedCompetitorId === comp.id ? 'bg-blue-600 text-white' : 'bg-zinc-900 text-zinc-400 hover:text-white'}`}
            >
              {comp.name}
              <span className="flex gap-1">
                <SafeIcon name="Edit" className="w-3 h-3 opacity-60 hover:opacity-100" onClick={(e) => { e.stopPropagation(); setEditingCompetitor({ ...comp }); }} />
                <SafeIcon name="Trash2" className="w-3 h-3 opacity-60 hover:opacity-100" onClick={(e) => { e.stopPropagation(); handleDeleteCompetitor(comp.id); }} />
              </span>
            </button>
          ))}
        </div>

        {competitors.length === 0 && (
          <p className="text-xs text-zinc-600 font-light">No competitors yet. Add one to start building swipe files for clients.</p>
        )}

        <div className="flex justify-end">
          <button
            onClick={() => setEditingAd({ ...EMPTY_AD, competitor_id: selectedCompetitorId !== 'all' ? selectedCompetitorId : (competitors[0]?.id || ''), isNew: true })}
            disabled={competitors.length === 0}
            className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 disabled:opacity-40 text-white px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2"
          >
            <SafeIcon name="Plus" className="w-4 h-4 text-blue-400" />
            Add Ad
          </button>
        </div>

        <div className="bg-zinc-950 border border-zinc-900 rounded-2xl overflow-hidden">
          <table className="w-full text-left text-xs">
            <thead className="bg-zinc-900/50 text-zinc-400 text-[10px] font-bold uppercase tracking-wider border-b border-zinc-900">
              <tr>
                <th className="p-4 pl-6">Competitor</th>
                <th className="p-4">Title</th>
                <th className="p-4">Platform</th>
                <th className="p-4">Status</th>
                <th className="p-4 pr-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900 text-zinc-300">
              {ads.map(ad => (
                <tr key={ad.id} className="hover:bg-zinc-900/20 transition-colors">
                  <td className="p-4 pl-6 font-bold text-white">{ad.competitor_name}</td>
                  <td className="p-4 max-w-xs truncate">{ad.title}</td>
                  <td className="p-4 uppercase text-zinc-500">{ad.platform}</td>
                  <td className="p-4">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest border ${ad.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                      {ad.status}
                    </span>
                  </td>
                  <td className="p-4 pr-6 text-right whitespace-nowrap">
                    <button onClick={() => setEditingAd({ ...ad })} className="p-2 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 rounded-lg mr-2">
                      <SafeIcon name="Edit" className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => handleDeleteAd(ad.id)} className="p-2 bg-zinc-900 hover:bg-red-950 text-zinc-500 hover:text-red-400 rounded-lg">
                      <SafeIcon name="Trash2" className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {ads.length === 0 && (
                <tr><td colSpan="5" className="py-10 text-center text-zinc-600 text-xs">No ads recorded for this selection yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Client Workspaces & Monitor Allocations */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-3xl p-8 shadow-2xl space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h2 className="text-xl font-serif font-bold text-white">Client Portal Workspaces</h2>
            <p className="text-xs text-zinc-500 mt-1 font-light">
              Assign which competitor monitors each client can see in their Maxmark Dashboard.
            </p>
          </div>
          <button
            onClick={() => setNewClient({ ...EMPTY_CLIENT })}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2 shadow-lg shadow-blue-600/20"
          >
            <SafeIcon name="Plus" className="w-4 h-4" />
            Add Client
          </button>
        </div>

        <div className="space-y-4">
          {clients.map(client => (
            <div key={client.id} className="bg-zinc-900/40 border border-zinc-900 rounded-2xl p-6 flex flex-col lg:flex-row justify-between lg:items-center gap-6">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-base font-bold text-white">{client.company_name}</h3>
                  <span className="bg-blue-600/10 text-blue-400 border border-blue-500/20 text-[9px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded">
                    {client.full_name}
                  </span>
                </div>
                <p className="text-xs text-zinc-500 mt-0.5">{client.email}</p>
              </div>

              <div className="space-y-1.5">
                <p className="text-[9px] uppercase font-bold text-zinc-500">Assigned Competitor Monitors</p>
                <div className="flex flex-wrap gap-2">
                  {competitors.map(comp => {
                    const isAssigned = client.assigned_competitor_ids.includes(comp.id);
                    return (
                      <button
                        key={comp.id}
                        type="button"
                        onClick={() => handleToggleAssignment(client.id, comp.id, isAssigned)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${isAssigned ? 'bg-blue-600 text-white shadow-sm' : 'bg-zinc-900 text-zinc-500 hover:text-zinc-300 border border-zinc-800'}`}
                      >
                        <SafeIcon name={isAssigned ? 'Check' : 'Plus'} className="w-3 h-3" />
                        {comp.name}
                      </button>
                    );
                  })}
                  {competitors.length === 0 && <span className="text-[10px] text-zinc-600">Add a competitor above to assign one.</span>}
                </div>
              </div>
            </div>
          ))}
          {clients.length === 0 && (
            <p className="text-xs text-zinc-600 font-light">No client accounts yet. Add one so they can sign in to the portal.</p>
          )}
        </div>
      </div>

      {/* Incoming Client Monitor Requests Queue */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-3xl p-8 shadow-2xl space-y-6">
        <div>
          <h2 className="text-xl font-serif font-bold text-white">Client Tracker Requests</h2>
          <p className="text-xs text-zinc-500 mt-1 font-light">
            When clients request a new brand to be tracked from their portal, it appears here for approval.
          </p>
        </div>

        <div className="space-y-3">
          {requests.map(req => (
            <div key={req.id} className="p-5 bg-zinc-900/40 border border-zinc-900 rounded-2xl flex flex-col md:flex-row justify-between md:items-center gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <h4 className="text-sm font-bold text-white">{req.competitor_name}</h4>
                  <span className="text-[10px] text-zinc-500">requested by <strong className="text-zinc-300">{req.company_name}</strong></span>
                  <span className="text-[9px] text-zinc-600">• {new Date(req.created_at).toLocaleDateString()}</span>
                </div>
                {req.competitor_websites && (
                  <a href={req.competitor_websites} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline block">
                    {req.competitor_websites}
                  </a>
                )}
                {req.competitive_concern && <p className="text-xs text-zinc-400 font-light mt-1 italic">"{req.competitive_concern}"</p>}
              </div>

              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={() => handleApproveRequest(req.id)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all"
                >
                  Approve & Track
                </button>
              </div>
            </div>
          ))}
          {requests.length === 0 && (
            <p className="text-xs text-zinc-600 font-light">No pending requests right now.</p>
          )}
        </div>
      </div>

      {/* Competitor Modal */}
      {editingCompetitor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-950 border border-zinc-900 rounded-3xl max-w-lg w-full p-8 space-y-6 shadow-2xl">
            <div className="flex justify-between items-center border-b border-zinc-900 pb-4">
              <h3 className="text-lg font-bold text-white">{editingCompetitor.isNew ? 'Add Competitor' : 'Edit Competitor'}</h3>
              <button onClick={() => setEditingCompetitor(null)} className="text-zinc-500 hover:text-white"><SafeIcon name="X" className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSaveCompetitor} className="space-y-4 text-xs">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Name</label>
                <input type="text" className="input-field" value={editingCompetitor.name} onChange={e => setEditingCompetitor({ ...editingCompetitor, name: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Website</label>
                <input type="text" className="input-field" value={editingCompetitor.website || ''} onChange={e => setEditingCompetitor({ ...editingCompetitor, website: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Industry</label>
                <input type="text" className="input-field" value={editingCompetitor.industry || ''} onChange={e => setEditingCompetitor({ ...editingCompetitor, industry: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Description</label>
                <textarea rows={3} className="input-field resize-none" value={editingCompetitor.description || ''} onChange={e => setEditingCompetitor({ ...editingCompetitor, description: e.target.value })} />
              </div>
              <div className="flex gap-3 pt-4 border-t border-zinc-900">
                <button type="button" onClick={() => setEditingCompetitor(null)} className="w-1/2 bg-zinc-900 text-zinc-400 hover:text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs">Cancel</button>
                <button type="submit" className="w-1/2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs shadow-lg shadow-blue-600/20">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ad Modal */}
      {editingAd && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-zinc-950 border border-zinc-900 rounded-3xl max-w-xl w-full p-8 space-y-6 shadow-2xl my-8">
            <div className="flex justify-between items-center border-b border-zinc-900 pb-4">
              <h3 className="text-lg font-bold text-white">{editingAd.isNew ? 'Add Ad' : 'Edit Ad'}</h3>
              <button onClick={() => setEditingAd(null)} className="text-zinc-500 hover:text-white"><SafeIcon name="X" className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSaveAd} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase">Competitor</label>
                  <select className="input-field" value={editingAd.competitor_id} onChange={e => setEditingAd({ ...editingAd, competitor_id: e.target.value })} required>
                    {competitors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase">Platform</label>
                  <select className="input-field" value={editingAd.platform} onChange={e => setEditingAd({ ...editingAd, platform: e.target.value })}>
                    <option value="meta">Meta</option>
                    <option value="google">Google</option>
                    <option value="tiktok">TikTok</option>
                    <option value="linkedin">LinkedIn</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase">Format</label>
                  <select className="input-field" value={editingAd.format} onChange={e => setEditingAd({ ...editingAd, format: e.target.value })}>
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                    <option value="carousel">Carousel</option>
                    <option value="text">Text</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase">Status</label>
                  <select className="input-field" value={editingAd.status} onChange={e => setEditingAd({ ...editingAd, status: e.target.value })}>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Title</label>
                <input type="text" className="input-field" value={editingAd.title} onChange={e => setEditingAd({ ...editingAd, title: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Ad Copy</label>
                <textarea rows={3} className="input-field resize-none" value={editingAd.copy || ''} onChange={e => setEditingAd({ ...editingAd, copy: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase">Landing Page URL</label>
                  <input type="text" className="input-field" value={editingAd.landing_page_url || ''} onChange={e => setEditingAd({ ...editingAd, landing_page_url: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-zinc-400 uppercase">CTA Text</label>
                  <input type="text" className="input-field" value={editingAd.cta_text || ''} onChange={e => setEditingAd({ ...editingAd, cta_text: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Analysis: Hook</label>
                <textarea rows={2} className="input-field resize-none" value={editingAd.analysis_hook || ''} onChange={e => setEditingAd({ ...editingAd, analysis_hook: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Analysis: Angle</label>
                <textarea rows={2} className="input-field resize-none" value={editingAd.analysis_angle || ''} onChange={e => setEditingAd({ ...editingAd, analysis_angle: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Analysis: Funnel</label>
                <textarea rows={2} className="input-field resize-none" value={editingAd.analysis_funnel || ''} onChange={e => setEditingAd({ ...editingAd, analysis_funnel: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Maxmark Advisory</label>
                <textarea rows={2} className="input-field resize-none" value={editingAd.analysis_advisory || ''} onChange={e => setEditingAd({ ...editingAd, analysis_advisory: e.target.value })} />
              </div>
              <div className="flex gap-3 pt-4 border-t border-zinc-900">
                <button type="button" onClick={() => setEditingAd(null)} className="w-1/2 bg-zinc-900 text-zinc-400 hover:text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs">Cancel</button>
                <button type="submit" className="w-1/2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs shadow-lg shadow-blue-600/20">Save Ad</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Client Modal */}
      {newClient && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-950 border border-zinc-900 rounded-3xl max-w-md w-full p-8 space-y-6 shadow-2xl">
            <div className="flex justify-between items-center border-b border-zinc-900 pb-4">
              <h3 className="text-lg font-bold text-white">Add Client</h3>
              <button onClick={() => setNewClient(null)} className="text-zinc-500 hover:text-white"><SafeIcon name="X" className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleCreateClient} className="space-y-4 text-xs">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Company Name</label>
                <input type="text" className="input-field" value={newClient.company_name} onChange={e => setNewClient({ ...newClient, company_name: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Contact Full Name</label>
                <input type="text" className="input-field" value={newClient.full_name} onChange={e => setNewClient({ ...newClient, full_name: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Login Email</label>
                <input type="email" className="input-field" value={newClient.email} onChange={e => setNewClient({ ...newClient, email: e.target.value })} required />
              </div>
              <p className="text-[10px] text-zinc-500 font-light">A temporary password will be generated - share it with the client securely.</p>
              <div className="flex gap-3 pt-4 border-t border-zinc-900">
                <button type="button" onClick={() => setNewClient(null)} className="w-1/2 bg-zinc-900 text-zinc-400 hover:text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs">Cancel</button>
                <button type="submit" className="w-1/2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs shadow-lg shadow-blue-600/20">Create Account</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Created Client Credentials (shown once) */}
      {createdClient && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-950 border border-zinc-900 rounded-3xl max-w-md w-full p-8 space-y-6 shadow-2xl">
            <div className="flex items-center gap-2 text-emerald-400">
              <SafeIcon name="CheckCircle" className="w-5 h-5" />
              <h3 className="text-lg font-bold text-white">Client Account Created</h3>
            </div>
            <p className="text-xs text-zinc-400 font-light">
              Share these credentials with {createdClient.full_name} - this password is only shown once.
            </p>
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 space-y-2 text-xs font-mono">
              <p><span className="text-zinc-500">Email:</span> <span className="text-white">{createdClient.email}</span></p>
              <p><span className="text-zinc-500">Password:</span> <span className="text-white">{createdClient.tempPassword}</span></p>
            </div>
            <button onClick={() => setCreatedClient(null)} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
